import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, App, Button, Card, Col, DatePicker, Form, Progress, Row, Space, Statistic, Table, Typography } from "antd";
import {
  FUNNEL_STAGES,
  type FunnelCohort,
  type FunnelResponse,
  type FunnelStageCounts,
  type FunnelUtmRow,
} from "../../types/funnel";

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string): Promise<T> {
  const response = await fetch(url, { headers: adminHeaders() });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

function OverallFunnel({ overall, paidAvailable }: { overall: FunnelStageCounts; paidAvailable: boolean }) {
  const signup = overall.signup || 0;
  return (
    <Card size="small" title="Lejek — ogółem" style={{ marginBottom: 24 }}>
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {FUNNEL_STAGES.map((stage) => {
          const value = overall[stage.key] ?? 0;
          const unavailable = stage.key === "paid" && !paidAvailable;
          return (
            <div key={stage.key}>
              <Row justify="space-between">
                <Col>
                  <Text strong>{stage.label}</Text>
                </Col>
                <Col>
                  {unavailable ? (
                    <Text type="secondary">brak danych</Text>
                  ) : (
                    <Text>
                      {value} <Text type="secondary">({pct(value, signup)}% rejestracji)</Text>
                    </Text>
                  )}
                </Col>
              </Row>
              <Progress
                percent={unavailable ? 0 : pct(value, signup)}
                showInfo={false}
                strokeColor={unavailable ? "#d9d9d9" : undefined}
              />
            </div>
          );
        })}
      </Space>
    </Card>
  );
}

const stageColumns = (paidAvailable: boolean) =>
  FUNNEL_STAGES.map((stage) => ({
    title: stage.label,
    dataIndex: stage.key,
    width: 150,
    render: (value: number) =>
      stage.key === "paid" && !paidAvailable ? <Text type="secondary">—</Text> : (value ?? 0),
  }));

export const FunnelList = () => {
  const { notification } = App.useApp();

  const [range, setRange] = useState<{ since?: string; until?: string }>({});
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (active: { since?: string; until?: string }) => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (active.since) query.set("since", active.since);
        if (active.until) query.set("until", active.until);
        const qs = query.toString();
        const response = await adminFetch<FunnelResponse>(
          `${API_URL}/api/v1/admin/funnel${qs ? `?${qs}` : ""}`,
        );
        setData(response);
      } catch (error) {
        setData(null);
        notification.error({
          message: "Nie udało się pobrać lejka",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    load({});
  }, [load]);

  const paidAvailable = data?.paid_source?.available ?? false;
  const cohortCols = useMemo(() => stageColumns(paidAvailable), [paidAvailable]);
  const utmCols = useMemo(() => stageColumns(paidAvailable), [paidAvailable]);

  return (
    <Card loading={loading && !data}>
      <Title level={4}>Lejek marketingowy</Title>
      <Paragraph type="secondary">
        Rejestracja → aktywacja (pierwsze wyszukanie) → engagement → checkout → płatność. Kohorty
        tygodniowe wg daty rejestracji, atrybucja UTM first-touch. Dane historyczne są kadłubowe —
        tracking dopiero ruszył — więc wcześniejsze etapy pokazują realny drop-off, nie brak danych.
      </Paragraph>

      <Form layout="inline" style={{ marginBottom: 24 }}>
        <Form.Item label="Kohorty rejestracji (zakres)">
          <RangePicker
            onChange={(dates) =>
              setRange({
                since: dates?.[0]?.startOf("day").toISOString(),
                until: dates?.[1]?.endOf("day").toISOString(),
              })
            }
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={() => load(range)}>
              Odśwież
            </Button>
            <Button
              onClick={() => {
                setRange({});
                load({});
              }}
            >
              Wyczyść
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {!paidAvailable && data && (
        <Alert
          type="info"
          showIcon
          message={"Etap „Płatność” bez danych"}
          description={
            "Źródło płatności (subskrypcje/StripeEvents w Supabase) nie odpowiedziało — etap paid " +
            "jest ukryty zamiast pokazywać mylące zero. Reszta lejka jest kompletna."
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {data && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Użytkownicy w kohortach" value={data.total_users} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Aktywacja"
                  value={pct(data.overall.activation, data.overall.signup)}
                  suffix="%"
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Checkout"
                  value={pct(data.overall.checkout_started, data.overall.signup)}
                  suffix="%"
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Bez daty rejestracji"
                  value={data.skipped_users_without_date}
                />
              </Card>
            </Col>
          </Row>

          <OverallFunnel overall={data.overall} paidAvailable={paidAvailable} />

          <Card size="small" title="Kohorty tygodniowe (wg daty rejestracji)" style={{ marginBottom: 24 }}>
            <Table<FunnelCohort>
              rowKey={(row) => row.week}
              dataSource={data.cohorts}
              pagination={false}
              size="small"
              scroll={{ x: true, y: 360 }}
              columns={[
                { title: "Tydzień (pon.)", dataIndex: "week", width: 140, fixed: "left" },
                ...cohortCols,
              ]}
            />
          </Card>

          <Card size="small" title="Atrybucja UTM (first-touch)">
            <Table<FunnelUtmRow>
              rowKey={(row) => row.source}
              dataSource={data.utm_sources}
              pagination={false}
              size="small"
              scroll={{ x: true }}
              columns={[
                { title: "Źródło UTM", dataIndex: "source", width: 180, fixed: "left" },
                ...utmCols,
              ]}
            />
          </Card>
        </>
      )}
    </Card>
  );
};
